import { BrowserSidebarTabs } from '@/components/browser/browser-sidebar-tabs';
import { BrowserPane } from '@/components/desktop-browser-pane';
import { PlacedTab } from '@/components/desktop-side-panel/placed-tab';
import type { BrowserSurface } from './platform-services';

/**
 * Renders every open browser tab, keeping them all mounted so page state survives tab switches:
 * only the tabs the panel shows are visible and marked `active` (which drives the native Electron
 * webview / iframe attachment), alone or each over its cell of the split.
 *
 * A tab an agent drives stays laid out even when not shown: transparent, under the visible
 * tab, out of pointer reach. Chromium neither paints nor captures a webview under
 * `display: none`, `visibility: hidden` or outside the window, but does when it is merely
 * covered or transparent — so the agent's screenshots never need the tab brought to the front.
 */
export function PlatformWebSurface({
  sessions,
  visible,
  activeBrowserId,
  placements,
  controlledBrowserIds,
  workspace,
  onBrowserSelection,
  onRenameSession,
  onSessionFavicon,
  onSessionModeChange,
  onSelectSession,
  onNewSession,
  onCloseSession,
}: BrowserSurface) {
  const anyDriven = sessions.some((session) => controlledBrowserIds.has(session.id));
  return (
    <>
      {visible && (
        <BrowserSidebarTabs
          activeId={activeBrowserId}
          controlledIds={controlledBrowserIds}
          onClose={onCloseSession}
          onNew={onNewSession}
          onSelect={onSelectSession}
          sessions={sessions}
        />
      )}
      <div
        aria-hidden={visible ? undefined : true}
        className={
          visible
            ? 'relative h-full'
            : anyDriven
              ? 'pointer-events-none fixed inset-0 -z-10 opacity-0'
              : 'hidden'
        }
      >
        {sessions.map((session) => {
          const placement = placements.get(session.id);
          return (
            <PlacedTab
              id={session.id}
              keepPainting={controlledBrowserIds.has(session.id)}
              key={session.id}
              onSelect={onSelectSession}
              placement={placement}
            >
              <BrowserPane
                active={placement !== undefined}
                selected={placement !== undefined && session.id === activeBrowserId}
                id={session.id}
                initialUrl={session.initialUrl}
                mode={session.mode}
                profileId={session.profileId}
                profile={session.profile}
                controlled={controlledBrowserIds.has(session.id)}
                onSelection={onBrowserSelection}
                onFaviconChange={(favicon) => onSessionFavicon(session.id, favicon)}
                onLabelChange={(name) => onRenameSession(session.id, name)}
                onNewTab={onNewSession}
                onClose={() => onCloseSession(session.id)}
                {...(placement ? { onPageFocus: () => onSelectSession(session.id) } : {})}
                onModeChange={(mode) => onSessionModeChange(session.id, mode)}
                workspaceRoot={workspace ?? 'crowdex-default-workspace'}
              />
            </PlacedTab>
          );
        })}
      </div>
    </>
  );
}
